import { WORDPRESS_API_BASE, WORDPRESS_BASE_URL } from '../config/wordpress';
import type { WPPost, WPCategory, WPTag, WPMedia, PostFilters, WPEvent, EventFilters, WPPage, PageFilters, WPToolkit, ToolkitFilters } from '../types/wordpress';
import { clerkAuthHeaders } from './clerkApiAuth';
import { resolveAcfAttachmentId } from '../utils/resourceAttachments';

class WordPressAPIService {
  private baseUrl = WORDPRESS_API_BASE;

  private async fetchWithErrorHandling<T>(url: string, init?: RequestInit): Promise<T> {
    try {
      const response = await fetch(url, init);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Resource not found');
        }
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      // Log for toolkit fetches to debug
      if (url.includes('/toolkit')) {
        console.log(`WordPress API response for ${url}:`, {
          count: Array.isArray(data) ? data.length : 'not an array',
          data: Array.isArray(data) ? data.map((t: any) => ({ id: t.id, title: t.title?.rendered || t.title, status: t.status })) : data
        });
      }
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  async fetchPosts(params?: PostFilters): Promise<WPPost[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const url = `${this.baseUrl}/posts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.fetchWithErrorHandling<WPPost[]>(url);
  }

  async fetchPost(id: number): Promise<WPPost> {
    return this.fetchWithErrorHandling<WPPost>(`${this.baseUrl}/posts/${id}`);
  }

  async fetchPostBySlug(slug: string): Promise<WPPost | null> {
    const posts = await this.fetchPosts({ slug, per_page: 1 });
    return posts.length > 0 ? posts[0] : null;
  }

  async fetchCategories(params?: { per_page?: number; page?: number; include?: string }): Promise<WPCategory[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${this.baseUrl}/categories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.fetchWithErrorHandling<WPCategory[]>(url);
  }

  async fetchTags(params?: { per_page?: number; page?: number }): Promise<WPTag[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${this.baseUrl}/tags${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.fetchWithErrorHandling<WPTag[]>(url);
  }

  async fetchMedia(id: number): Promise<WPMedia> {
    return this.fetchWithErrorHandling<WPMedia>(`${this.baseUrl}/media/${id}`);
  }

  async fetchMediaList(params?: { search?: string; per_page?: number; page?: number }): Promise<WPMedia[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${this.baseUrl}/media${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.fetchWithErrorHandling<WPMedia[]>(url);
  }

  async fetchAttachments(postId: number): Promise<WPMedia[]> {
    const noStore: RequestInit = { cache: 'no-store' };

    let post: WPPost | null = null;
    try {
      post = await this.fetchWithErrorHandling<WPPost>(
        `${this.baseUrl}/posts/${postId}`,
        noStore
      );
    } catch {
      // Parent-media fallback still works without the post payload
    }

    // Prefer the current ACF file. Replaced uploads often remain as parented media.
    const acfAttachmentId = resolveAcfAttachmentId(post?.acf?.attachment);
    if (acfAttachmentId != null) {
      try {
        return [await this.fetchMedia(acfAttachmentId)];
      } catch {
        // Stale ACF id — fall through to parented media
      }
    }

    const byParent = await this.fetchWithErrorHandling<WPMedia[]>(
      `${this.baseUrl}/media?parent=${postId}&per_page=100`,
      noStore
    );
    const byId = new Map<number, WPMedia>(byParent.map((item) => [item.id, item]));

    if (post?.featured_media) {
      try {
        const featured = await this.fetchMedia(post.featured_media);
        if (!byId.has(featured.id)) {
          byId.set(featured.id, featured);
        }
      } catch {
        // Featured media is optional for the attachment list
      }
    }

    return Array.from(byId.values());
  }

  /**
   * Upload an image through the Express proxy → WordPress Media Library.
   * Credentials stay on the server (WP_USER / WP_APP_PASSWORD).
   */
  async uploadMedia(
    file: File,
    options?: { alt?: string }
  ): Promise<{ id: number; source_url: string; alt_text: string; mime_type?: string; title?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.alt) {
      formData.append('alt', options.alt);
    }

    const response = await fetch('/api/wordpress/media', {
      method: 'POST',
      headers: await clerkAuthHeaders({ json: false }),
      body: formData,
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error || `Failed to upload media: ${response.statusText}`);
    }

    return payload;
  }

  async search(query: string, params?: { per_page?: number; page?: number }): Promise<WPPost[]> {
    // Use the posts endpoint with search parameter to get full post objects
    const queryParams = new URLSearchParams();
    queryParams.append('search', query);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${this.baseUrl}/posts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.fetchWithErrorHandling<WPPost[]>(url);
  }

  async fetchEvents(params?: EventFilters): Promise<WPEvent[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${this.baseUrl}/tribe_events${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.fetchWithErrorHandling<WPEvent[]>(url);
  }

  async fetchEvent(id: number): Promise<WPEvent> {
    return this.fetchWithErrorHandling<WPEvent>(`${this.baseUrl}/tribe_events/${id}`);
  }

  async fetchEventBySlug(slug: string): Promise<WPEvent | null> {
    const events = await this.fetchEvents({ per_page: 100 });
    const event = events.find((e) => e.slug === slug);
    if (event) return event;

    // Fallback: try TEC REST API (handles past events the WP API doesn't return)
    try {
      const res = await fetch(
        `${WORDPRESS_BASE_URL}/wp-json/tribe/events/v1/events/by-slug/${slug}`
      );
      if (!res.ok) return null;
      const e = await res.json();
      const decodeHTML = (html: string) =>
        new DOMParser().parseFromString(html || '', 'text/html').body.textContent || '';
      return {
        id: e.id,
        date: e.date,
        modified: e.modified || '',
        slug: e.slug,
        link: e.url || '',
        title: { rendered: decodeHTML(e.title) },
        content: { rendered: e.description || '' },
        excerpt: { rendered: e.excerpt || '' },
        featured_media: e.image?.id || 0,
        author: e.author || 0,
        tags: [],
        tribe_events_cat: e.categories?.map((c: { id: number }) => c.id) || [],
        meta: {
          _EventStartDate: e.start_date,
          _EventEndDate: e.end_date,
          _EventAllDay: e.all_day,
          _EventURL: e.website || undefined,
          _VenueAddress: e.venue?.address || undefined,
          _VenueCity: e.venue?.city || undefined,
          _VenueState: e.venue?.state || undefined,
        },
      };
    } catch {
      return null;
    }
  }

  async fetchPages(params?: PageFilters): Promise<WPPage[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${this.baseUrl}/pages${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.fetchWithErrorHandling<WPPage[]>(url);
  }

  async fetchPage(id: number): Promise<WPPage> {
    return this.fetchWithErrorHandling<WPPage>(`${this.baseUrl}/pages/${id}`);
  }

  async fetchPageBySlug(slug: string): Promise<WPPage | null> {
    const pages = await this.fetchPages({ slug, per_page: 1 });
    return pages.length > 0 ? pages[0] : null;
  }

  // Toolkit post type methods (WordPress content only)
  async fetchToolkits(params?: ToolkitFilters): Promise<WPToolkit[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${this.baseUrl}/toolkit${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log('Fetching toolkits from:', url);
    const result = await this.fetchWithErrorHandling<WPToolkit[]>(url);
    console.log('WordPress API returned', result.length, 'toolkits');
    return result;
  }

  async fetchToolkit(id: number): Promise<WPToolkit> {
    return this.fetchWithErrorHandling<WPToolkit>(`${this.baseUrl}/toolkit/${id}`);
  }

  async fetchToolkitBySlug(slug: string): Promise<WPToolkit | null> {
    const toolkits = await this.fetchToolkits({ slug, per_page: 1 });
    return toolkits.length > 0 ? toolkits[0] : null;
  }
}

export const wordpressAPI = new WordPressAPIService();

